import { httpsCallable } from "firebase/functions";
import { auth, functions } from "../app/firebase";

export const ARTICLE_IMAGE_MAX_WIDTH = 1200;
export const ARTICLE_IMAGE_JPEG_QUALITY = 0.85;
const UPLOAD_TIMEOUT_MS = 90_000;

/** Reject bare filenames, data URLs, file:// — mobile expects full HTTPS download URLs. */
export function isValidArticleImageUrl(url) {
  if (!url || typeof url !== "string") return false;
  const t = url.trim();
  if (!t.startsWith("https://")) return false;
  if (t.startsWith("data:") || t.startsWith("file://")) return false;
  return true;
}

export function articleCoverPath(articleId, ext = "jpg") {
  return `articles/${articleId}/cover.${ext}`;
}

export function articleCardImagePath(articleId, cardId, ext = "jpg") {
  return `articles/${articleId}/cards/${cardId}.${ext}`;
}

export function articleInlineImagePath(articleId, imageId, ext = "jpg") {
  return `articles/${articleId}/web/${imageId}.${ext}`;
}

async function ensureAdminUpload() {
  const user = auth.currentUser;
  if (!user) {
    throw new Error("Sign in to upload images.");
  }
  const token = await user.getIdTokenResult();
  if (!token.claims.admin) {
    throw new Error("Admin access required to upload article images.");
  }
}

function blobToBase64(blob) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const result = reader.result;
      if (typeof result !== "string" || !result.includes(",")) {
        reject(new Error("Failed to read image"));
        return;
      }
      resolve(result.split(",")[1]);
    };
    reader.onerror = () => reject(reader.error || new Error("Failed to read image"));
    reader.readAsDataURL(blob);
  });
}

function withTimeout(promise, ms, message) {
  return Promise.race([
    promise,
    new Promise((_, reject) => {
      setTimeout(() => reject(new Error(message)), ms);
    }),
  ]);
}

function formatUploadError(err) {
  const code = err?.code || "";
  const msg = err?.message || String(err);
  if (
    code === "functions/not-found" ||
    msg.includes("uploadArticleImageAdmin")
  ) {
    return (
      "Upload function is not deployed. Run: firebase deploy --only functions:uploadArticleImageAdmin"
    );
  }
  if (code === "functions/permission-denied" || msg.includes("permission")) {
    return "Admin access required. Check that your account has the admin claim.";
  }
  if (msg.includes("CORS") || msg.includes("storage/unauthorized")) {
    return (
      "Storage access denied. Deploy storage rules: firebase deploy --only storage"
    );
  }
  return msg || "Upload failed";
}

/**
 * Resize for upload. Transparent PNG/WebP are flattened onto white, then saved as JPEG.
 * @returns {Promise<{ blob: Blob, mimeType: 'image/jpeg', ext: 'jpg' }>}
 */
export async function optimizeImageFile(file) {
  const bitmap = await createImageBitmap(file);
  const scale = Math.min(1, ARTICLE_IMAGE_MAX_WIDTH / bitmap.width);
  const width = Math.round(bitmap.width * scale);
  const height = Math.round(bitmap.height * scale);

  const canvas = document.createElement("canvas");
  canvas.width = width;
  canvas.height = height;
  const ctx = canvas.getContext("2d");
  if (!ctx) throw new Error("Canvas unsupported");
  ctx.fillStyle = "#ffffff";
  ctx.fillRect(0, 0, width, height);
  ctx.drawImage(bitmap, 0, 0, width, height);
  bitmap.close?.();

  return new Promise((resolve, reject) => {
    canvas.toBlob(
      (blob) =>
        blob
          ? resolve({ blob, mimeType: "image/jpeg", ext: "jpg" })
          : reject(new Error("Image encode failed")),
      "image/jpeg",
      ARTICLE_IMAGE_JPEG_QUALITY,
    );
  });
}

async function deleteStoragePaths(paths) {
  await ensureAdminUpload();
  const callable = httpsCallable(functions, "deleteArticleStorageAdmin");
  await callable({ paths });
}

export async function deleteStoragePath(path) {
  await deleteStoragePaths([path]);
}

/**
 * Upload via Cloud Function (Admin SDK) — avoids Storage CORS / rules issues in browser.
 * @param {{ articleId: string, kind: 'cover' | 'card' | 'inline', cardId?: string, imageId?: string, file: File }} params
 * @returns {Promise<string>} Firebase Storage download URL for Firestore
 */
export async function uploadArticleImage({ articleId, kind, cardId, imageId, file }) {
  if (kind === "card" && !cardId) {
    throw new Error("cardId required for card image");
  }
  if (kind === "inline" && !imageId) {
    throw new Error("imageId required for inline image");
  }

  await ensureAdminUpload();
  const { blob, mimeType, ext } = await optimizeImageFile(file);
  const imageBase64 = await blobToBase64(blob);

  const callable = httpsCallable(functions, "uploadArticleImageAdmin");
  try {
    const result = await withTimeout(
      callable({
        articleId,
        kind,
        cardId: cardId || null,
        imageId: imageId || null,
        imageBase64,
        mimeType,
        ext,
      }),
      UPLOAD_TIMEOUT_MS,
      "Upload timed out. Check network or deploy uploadArticleImageAdmin.",
    );
    const downloadUrl = result?.data?.downloadUrl;
    if (!downloadUrl || !isValidArticleImageUrl(downloadUrl)) {
      throw new Error("Upload succeeded but returned an invalid URL");
    }
    return downloadUrl;
  } catch (err) {
    throw new Error(formatUploadError(err));
  }
}

export async function deleteArticleCover(articleId) {
  await deleteStoragePaths([
    articleCoverPath(articleId, "jpg"),
    articleCoverPath(articleId, "png"),
  ]);
}

export async function deleteArticleCardImage(articleId, cardId) {
  if (!cardId) return;
  await deleteStoragePaths([
    articleCardImagePath(articleId, cardId, "jpg"),
    articleCardImagePath(articleId, cardId, "png"),
  ]);
}

export async function deleteArticleStorageFiles(articleId, cardIds = []) {
  const paths = [
    articleCoverPath(articleId, "jpg"),
    articleCoverPath(articleId, "png"),
  ];
  const ids = Array.isArray(cardIds) ? cardIds : [];
  ids
    .filter((id) => typeof id === "string" && id)
    .forEach((cardId) => {
      paths.push(articleCardImagePath(articleId, cardId, "jpg"));
      paths.push(articleCardImagePath(articleId, cardId, "png"));
    });
  await deleteStoragePaths(paths);
}
